/** errorDetail JSON 文字列から表示用ステータス詳細を解析して返す
 *
 * バックエンドから返却される errorDetail のフォーマットは以下の3パターン：
 *   パターン①: statusDetails 配列を含む JSON 文字列（正常系エラー）
 *   パターン②: details.response.status.message または details.response.message を含む JSON 文字列（4xx/5xx エラー）
 *   パターン③: その他（message のみの JSON 等）
 *
 * null/空/JSONパース失敗/非オブジェクトは想定外のエラーとして id に "Exception Error" を設定する
 *
 * @param errorDetail バックエンドから返却される errorDetail 文字列
 * @param serverErrorLabel パターン③のフォールバック時に id として使用するラベル（例: "GPU Server Error"）
 * @returns 表示用ステータス詳細
 */
export function parseErrorDetail(
  errorDetail: string | null | undefined,
  serverErrorLabel: string
): { id: string | null; message: string | null; timestamp: string | null } {
  if (!errorDetail) {
    return { id: "Exception Error", message: null, timestamp: null };
  }

  let parsed: Record<string, unknown>;
  try {
    const result = JSON.parse(errorDetail);
    if (typeof result !== "object" || result === null) {
      return { id: "Exception Error", message: errorDetail, timestamp: null };
    }
    parsed = result;
  } catch {
    return { id: "Exception Error", message: errorDetail, timestamp: null };
  }

  // パターン①: statusDetails 配列の末尾要素を使用（正常系エラー）
  const statusDetails = parsed.statusDetails;
  if (Array.isArray(statusDetails) && statusDetails.length > 0) {
    const lastStatus = statusDetails[statusDetails.length - 1];
    if (typeof lastStatus !== "object" || lastStatus === null) {
      return {
        id: "Exception Error",
        message: errorDetail,
        timestamp: null,
      };
    }
    const last = lastStatus as Record<string, unknown>;
    return {
      id: (last.id as string) ?? null,
      message: (last.message as string) ?? null,
      timestamp: (last.timestamp as string) ?? null,
    };
  }

  const details = parsed.details as Record<string, unknown> | undefined;
  const response = details?.response as Record<string, unknown> | undefined;
  const status = response?.status as Record<string, unknown> | undefined;

  // パターン②: 4xx/5xx エラー（details.response.status.message が存在する場合）
  if (status?.message) {
    return {
      id: (parsed.code as string) ?? null,
      message: status.message as string,
      timestamp: null,
    };
  }

  // パターン②: 4xx/5xx エラー（details.response.message が存在する場合）
  if (response?.message) {
    return {
      id: (parsed.code as string) ?? null,
      message: response.message as string,
      timestamp: null,
    };
  }

  // パターン③: その他（最上位 message を理由欄に表示、ステータスは固定文言）
  return {
    id: serverErrorLabel,
    message: (parsed.message as string) ?? null,
    timestamp: null,
  };
}
